import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    List,
    ListItem,
    ListItemText,
} from '@mui/material';

import type { ChangeLogEntityType } from '@lifeserver/shared';

import { useChangeLogsByEntity } from '../hooks/changeLog';

import styles from './ChangeLogModal.module.scss';

type ChangelogModalProps = {
    open: boolean;
    onClose: () => void;
    entity: ChangeLogEntityType;
    entityId?: string;
    title?: string;
};

export const ChangelogModal = ({
    open,
    onClose,
    entity,
    entityId,
    title,
}: ChangelogModalProps) => {
    const { data, isLoading, error } = useChangeLogsByEntity(entity, entityId);

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>{title ?? 'Change Log'}</DialogTitle>
            <DialogContent>
                {isLoading && <div>Loading</div>}
                {error && <div>Could not get any changelog entries</div>}
                <List>
                    {(Array.isArray(data) ? data : []).map((entry) => (
                        <ListItem key={entry.id} alignItems="flex-start">
                            <ListItemText
                                primary={`${entry.action} by ${entry.userId} on ${new Date(entry.createdAt).toLocaleString()}`}
                                secondary={(() => {
                                    let parsed;
                                    try {
                                        parsed = JSON.parse(
                                            entry.details ?? '{}',
                                        );
                                    } catch {
                                        return entry.details;
                                    }

                                    if (
                                        parsed.action === 'updated' &&
                                        parsed.old &&
                                        parsed.updated
                                    ) {
                                        const keys = Array.from(
                                            new Set([
                                                ...Object.keys(parsed.old),
                                                ...Object.keys(parsed.updated),
                                            ]),
                                        );
                                        return (
                                            <table
                                                className={
                                                    styles.changelogTable
                                                }
                                            >
                                                <thead>
                                                    <tr>
                                                        <th>Field</th>
                                                        <th>Old</th>
                                                        <th>New</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {keys.map((key) => (
                                                        <tr key={key}>
                                                            <td>{key}</td>
                                                            <td>
                                                                {JSON.stringify(
                                                                    parsed.old[
                                                                        key
                                                                    ],
                                                                )}
                                                            </td>
                                                            <td>
                                                                {JSON.stringify(
                                                                    parsed
                                                                        .updated[
                                                                        key
                                                                    ],
                                                                )}
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        );
                                    }

                                    if (
                                        parsed.action === 'created' &&
                                        parsed.created
                                    ) {
                                        return (
                                            <div>
                                                <b>Created:</b>
                                                <pre
                                                    className={
                                                        styles.changelogPre
                                                    }
                                                >
                                                    {JSON.stringify(
                                                        parsed.created,
                                                        null,
                                                        2,
                                                    )}
                                                </pre>
                                            </div>
                                        );
                                    }

                                    if (
                                        parsed.action === 'deleted' &&
                                        parsed.deleted
                                    ) {
                                        return (
                                            <div>
                                                <b>Deleted:</b>
                                                <pre
                                                    className={
                                                        styles.changelogPre
                                                    }
                                                >
                                                    {JSON.stringify(
                                                        parsed.deleted,
                                                        null,
                                                        2,
                                                    )}
                                                </pre>
                                            </div>
                                        );
                                    }

                                    return (
                                        <pre className={styles.changelogPre}>
                                            {JSON.stringify(parsed, null, 2)}
                                        </pre>
                                    );
                                })()}
                            />
                        </ListItem>
                    ))}
                </List>
                {data?.length === 0 && !isLoading && (
                    <div>No history found</div>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Close</Button>
            </DialogActions>
        </Dialog>
    );
};
